<template>

<div class="share">

	<!-- the spoiler-free result grid, on screen -->
	<div class="share-card">
		<div class="share-header">{{ headerLine }}</div>
		<div class="share-grid">
			<span
				v-for="(c, i) in cells"
				:key="i"
				class="share-cell"
				:class="'is-' + c"
			></span>
		</div>
	</div>

	<div class="share-actions">

		<app-btn kind="primary" :block="true" v-on:click="copy">
			{{ copied ? 'Copied!' : 'Copy' }}
		</app-btn>

		<app-btn v-if="canShare" kind="ghost" :block="true" v-on:click="shareNative">
			Share
		</app-btn>

		<app-btn kind="ghost" :block="true" :loading="saving" v-on:click="save">
			Save image
		</app-btn>

	</div>

	<p v-if="copied" class="share-note">Result copied to your clipboard.</p>
	<p v-else-if="error" class="share-note is-error">{{ error }}</p>

</div>

</template>

<script>

/*
	The share block on the End screen: the spoiler-free result grid (4x4, filled
	in guess order — wrong/correct/empty) shown on screen, plus Copy (emoji text),
	Share (native share of the image/text), and Save image (canvas PNG). All
	artifacts are spoiler-free (no art, no name). The enriched `result` (with
	guesses/tiles) and `number` come from the End screen.
*/

import share from '../../service/share'

export default {

	props: {
		result: {
			default: null
		},
		number: {
			default: null
		}
	},

	data: function() {

		return {
			copied: false,
			saving: false,
			error: '',
			copiedTimer: null
		}

	},

	computed: {

		cells: function() {

			return share.cells(this.result)

		},

		headerLine: function() {

			return share.header(this.result, this.number)

		},

		canShare: function() {

			return !!(navigator && navigator.share)

		}

	},

	methods: {

		copy: function() {

			var self = this
			var text = share.grid(this.result, this.number)

			this.error = ''

			this.writeClipboard(text).then(function() {

				self.flagCopied()

			}).catch(function() {

				self.error = 'Couldn’t copy automatically. Long-press to copy the text.'

			})

		},

		// native share — prefer the image, fall back to the emoji text
		shareNative: function() {

			var self = this

			share.image(this.result, this.number).then(function(blob) {

				var data = { text: share.grid(self.result, self.number) }

				if (blob && window.File && navigator.canShare) {

					var file = new File([blob], 'whatboxart-' + (self.number || 'result') + '.png', { type: 'image/png' })

					if (navigator.canShare({ files: [file] })) data.files = [file]

				}

				if (navigator.share) navigator.share(data).catch(function() {})

			})

		},

		writeClipboard: function(text) {

			if (navigator.clipboard && navigator.clipboard.writeText) {

				return navigator.clipboard.writeText(text)

			}

			return new Promise(function(resolve, reject) {

				try {

					var area = document.createElement('textarea')
					area.value = text
					area.setAttribute('readonly', '')
					area.style.position = 'absolute'
					area.style.left = '-9999px'
					document.body.appendChild(area)
					area.select()

					var ok = document.execCommand('copy')
					document.body.removeChild(area)

					ok ? resolve() : reject(new Error('copy failed'))

				} catch (e) {

					reject(e)

				}

			})

		},

		flagCopied: function() {

			var self = this

			this.copied = true

			if (this.copiedTimer) clearTimeout(this.copiedTimer)

			this.copiedTimer = setTimeout(function() {

				self.copied = false

			}, 2200)

		},

		save: function() {

			var self = this

			this.error = ''
			this.saving = true

			share.image(this.result, this.number).then(function(blob) {

				self.saving = false

				if (!blob) {

					self.error = 'Couldn’t generate the image.'
					return

				}

				self.download(blob)

			}).catch(function() {

				self.saving = false
				self.error = 'Couldn’t generate the image.'

			})

		},

		download: function(blob) {

			var url = URL.createObjectURL(blob)
			var no = (this.number != null && this.number !== '') ? this.number : 'result'

			var link = document.createElement('a')
			link.href = url
			link.download = 'whatboxart-' + no + '.png'
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)

			setTimeout(function() {

				URL.revokeObjectURL(url)

			}, 1000)

		}

	},

	beforeDestroy: function() {

		if (this.copiedTimer) clearTimeout(this.copiedTimer)

	}

}

</script>

<style scoped>

.share {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

/* the on-screen result card */
.share-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 10px;
	padding: 16px;
	background: var(--paper);
	border: 1px solid var(--hairline);
	border-radius: var(--radius);
}

.share-header {
	font-family: var(--font-mono);
	font-size: 11px;
	letter-spacing: 0.04em;
	color: var(--muted-2);
	text-align: center;
}

.share-grid {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 5px;
}

.share-cell {
	width: 26px;
	height: 26px;
	border-radius: 4px;
}

.share-cell.is-wrong {
	background: var(--wrong);
}

.share-cell.is-correct {
	background: var(--correct);
}

.share-cell.is-empty {
	background: var(--hairline);
}

.share-actions {
	display: flex;
	gap: 10px;
}

.share-note {
	margin: 0;
	font-family: var(--font-body);
	font-size: 13px;
	color: var(--muted-2);
	text-align: center;
}

.share-note.is-error {
	color: var(--wrong);
}

</style>
